import router from 'next/router'
import React, { useState } from 'react'
import useMemoizedPathFor from '../../../lib/pathFor'
import useEventCrud from '../../../lib/useEventCrud'
import { useLeavePageConfirmation } from '../../../lib/useLeavePageConfirmation'
import useUser from '../../../lib/useUser'
import Icon from '../../common/Icon'
import BaseModal from '../../common/modal/BaseModal'
import ConfirmModal from '../../common/modal/ConfirmModal'
import CreateActivityModal from '../../common/modal/CreateActivityModal'
import AddToShowcase from '../../common/popout/AddToShowcase'
import CopyButton from '../../common/popout/CopyButton'
import FollowButton from '../../common/popout/FollowButton'
import {
  Popout,
  PopoutContent,
  PopoutTrigger,
} from '../../common/popout/Popout'
import PopoutButton from '../../common/popout/PopoutButton'
import EditEvent from '../../forms/EditEvent'
import ReportAction from '../../profile/common/ReportAction'
import { useTranslation } from '../../../lib/i18n'

const EventActions = ({ event }) => {
  const { user, isGroupManager } = useUser()
  const { t } = useTranslation()
  const shareLink = `https://${window.location.host}${useMemoizedPathFor(
    'event',
    event?.slug
  )}`
  const isAuthor = user?.id === event?.owner?.id
  const isManager =
    event?.eventable_type?.toLowerCase() === 'group'
      ? isGroupManager(event?.eventable?.id)
      : false
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false)
  const [editVisible, setEditVisible] = useState(false)
  const [activityModalVisible, setActivityModalVisible] = useState(false)
  const [modalChangesWithInput, setModalChangesWithInput] = useState(false)
  const attendeesHref = useMemoizedPathFor('attendees', event)

  const { deleteEvent } = useEventCrud()

  useLeavePageConfirmation(
    modalChangesWithInput && editVisible,
    t('common.discardChangesConfirmation')
  )

  const onDelete = async (eventId) => {
    try {
      setDeleteConfirmVisible(false)
      await deleteEvent(eventId)
      if (window.location.pathname.includes('/events/')) {
        router.replace('/')
      }
    } catch (error) {
      console.error('An unexpected error happened:', error)
    }
  }

  const toggleActivityModal = () => {
    setActivityModalVisible((prevState) => !prevState)
  }

  return (
    <Popout>
      <PopoutTrigger>
        <div className="flex flex-row items-center">
          <Icon name="more" color="offBlack" />
        </div>
      </PopoutTrigger>
      <PopoutContent>
        <CopyButton copyable={shareLink} label={t('activity.copyLink')} />
        {event?.status?.toLowerCase() !== 'not applicable' && (
          <FollowButton item={event} />
        )}
        <AddToShowcase
          id={event?.slug || event?.id}
          type={event?.type}
          visibility={event?.eventable?.visibility}
          badges={event?.eventable?.badges}
        />
        <PopoutButton onClick={() => router.push(attendeesHref)}>
          {t('event.viewAttendees')}
        </PopoutButton>
        <ReportAction type="Event" id={event?.id} isPopout />
        {isAuthor && (
          <>
            <PopoutButton onClick={() => setEditVisible(true)}>
              {t('event.editEvent')}
            </PopoutButton>
            <PopoutButton onClick={() => toggleActivityModal()}>
              {t('event.duplicateEvent')}
            </PopoutButton>
            <EditModal
              isOpen={editVisible}
              onRequestClose={() => setEditVisible(false)}
              event={event}
              parentId={event?.id}
              modalChangesWithInput={modalChangesWithInput}
              setModalChangesWithInput={setModalChangesWithInput}
            />
            <CreateActivityModal
              toggleModal={toggleActivityModal}
              isVisible={activityModalVisible}
              postInId={event?.eventable?.id}
              postInSlug={event?.eventable?.slug}
              postInType={event?.eventable_type?.toLowerCase()}
              itemType={event?.type.toLowerCase()}
              item={event}
            />
          </>
        )}
        {(isAuthor || isManager) && (
          <>
            <PopoutButton
              className="text-topic-pink"
              onClick={() => setDeleteConfirmVisible(true)}
            >
              {t('event.deleteEvent')}
            </PopoutButton>
            <ConfirmModal
              title={t('event.deleteEventConfirm')}
              subtitle={
                event.eventable?.name
                  ? t('event.deleteEventConfirmationWithGroup', {
                      group: event.eventable?.name,
                    })
                  : t('event.deleteEventConfirmation')
              }
              actionLabel={t('ui.actions.delete')}
              danger
              cancelLabel={t('ui.actions.cancel')}
              action={() => onDelete(event?.slug)}
              isOpen={deleteConfirmVisible}
              onRequestClose={() => setDeleteConfirmVisible(false)}
            />
          </>
        )}
      </PopoutContent>
    </Popout>
  )
}

function EditModal({
  isOpen,
  onRequestClose,
  event,
  modalChangesWithInput,
  setModalChangesWithInput,
}) {
  const { t } = useTranslation()
  const handleCloseEditModal = async () => {
    if (modalChangesWithInput && isOpen) {
      const confirm = await window.confirm(
        t('common.discardChangesConfirmation')
      )
      if (confirm) {
        onRequestClose()
      }
    } else {
      onRequestClose()
    }
  }

  return (
    <BaseModal isOpen={isOpen} onRequestClose={handleCloseEditModal}>
      <EditEvent
        event={event}
        toggleModal={onRequestClose}
        setModalChangesWithInput={setModalChangesWithInput}
      />
    </BaseModal>
  )
}

export default EventActions
